import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SafeImage } from '@/components/ui/safe-image';
import { Loader2, Tag, X } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { FloorplanZone, StoreImage } from '../types';
import { imagesForZone, untaggedImages } from '../lib/floorplanZoneUtils';

interface FloorplanPhotoTaggerProps {
  storeId: number;
  storeIdParam: string;
  images: StoreImage[];
  zones: FloorplanZone[];
  imageZoneTags: Record<string, string>;
  selectedZoneId: string | null;
  onSelectedZoneChange: (zoneId: string | null) => void;
}

async function invalidateFloorplanQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  storeIdParam: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
    queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
  ]);
}

export function FloorplanPhotoTagger({
  storeId,
  storeIdParam,
  images,
  zones,
  imageZoneTags,
  selectedZoneId,
  onSelectedZoneChange,
}: FloorplanPhotoTaggerProps) {
  const [pendingImageId, setPendingImageId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const selectedZone = zones.find((zone) => zone.id === selectedZoneId) ?? null;
  const taggedForZone = selectedZone ? imagesForZone(images, selectedZone.id, imageZoneTags) : [];
  const untagged = untaggedImages(images, imageZoneTags);

  const saveTag = async (imageId: string, zoneId: string | null) => {
    setPendingImageId(imageId);
    try {
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/floorplan/image-tags`,
        'PATCH',
        { imageId, zoneId },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to tag photo');
      }
      await invalidateFloorplanQueries(queryClient, storeIdParam);
    } catch (error) {
      toast({
        title: 'Could not tag photo',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setPendingImageId(null);
    }
  };

  if (zones.length === 0) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-gray-500">
          Add posting instruction locations as zones before tagging photos.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Tag photos to zones</CardTitle>
        <p className="text-sm text-gray-500">
          Select a zone on the floorplan, then assign posting evidence photos to it.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {selectedZone ? (
          <div className="flex items-center justify-between gap-2 rounded-lg border bg-blue-50 px-3 py-2">
            <div>
              <p className="text-sm font-medium">{selectedZone.label}</p>
              {selectedZone.instoreLocationId != null && (
                <p className="text-xs text-gray-500">Posting location #{selectedZone.instoreLocationId}</p>
              )}
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => onSelectedZoneChange(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Click a zone on the floorplan to start tagging.</p>
        )}

        {selectedZone && (
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium mb-2">Tagged here ({taggedForZone.length})</p>
              {taggedForZone.length === 0 ? (
                <p className="text-sm text-gray-500">No photos tagged to this zone yet.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {taggedForZone.map((image) => (
                    <div key={image.id} className="relative group">
                      <SafeImage
                        src={image.url}
                        alt={image.caption}
                        className="w-full aspect-square object-cover rounded-md border"
                        placeholderType="photo"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="absolute bottom-1 right-1 h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={pendingImageId === image.id}
                        onClick={() => void saveTag(image.id, null)}
                      >
                        {pendingImageId === image.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Remove'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Untagged photos ({untagged.length})</p>
              {untagged.length === 0 ? (
                <p className="text-sm text-gray-500">All posting photos are tagged.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto">
                  {untagged.map((image) => (
                    <button
                      key={image.id}
                      type="button"
                      className="relative rounded-md border overflow-hidden hover:ring-2 hover:ring-blue-400"
                      disabled={pendingImageId === image.id}
                      onClick={() => void saveTag(image.id, selectedZone.id)}
                    >
                      <SafeImage
                        src={image.url}
                        alt={image.caption}
                        className="w-full aspect-square object-cover"
                        placeholderType="photo"
                      />
                      <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate">
                        {image.caption}
                      </span>
                      {pendingImageId === image.id && (
                        <span className="absolute inset-0 flex items-center justify-center bg-white/70">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {!selectedZone && untagged.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">
              <Tag className="h-3 w-3 mr-1" />
              {untagged.length} untagged photo{untagged.length === 1 ? '' : 's'}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
